import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackPageView } from '../../firebase/trackingService';
import SEO from '../common/SEO';
import Button from '../common/Button';

const PageContainer = styled.section`
  background-color: #121212;
  color: #FFFFFF;
  padding: 120px 24px 80px;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
`;

const Content = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 42px;
  color: #2196F3;
  margin-bottom: 16px;
  font-weight: 700;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 48px;
  font-size: 18px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ContactInfo = styled.div`
  background-color: #1E1E1E;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
`;

const ContactForm = styled.div`
  background-color: #1E1E1E;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #FFD700;
  margin-bottom: 24px;
  font-weight: 600;
`;

const ContactItem = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactLabel = styled.h3`
  font-size: 16px;
  color: #2196F3;
  margin-bottom: 8px;
  font-weight: 600;
`;

const ContactValue = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  
  a {
    color: #2196F3;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #FFFFFF;
`;

const Input = styled.input`
  padding: 14px 16px;
  background-color: #2A2A2A;
  border: 1px solid #3A3A3A;
  border-radius: 8px;
  font-size: 16px;
  color: #FFFFFF;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 1px #2196F3;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  padding: 14px 16px;
  background-color: #2A2A2A;
  border: 1px solid #3A3A3A;
  border-radius: 8px;
  font-size: 16px;
  color: #FFFFFF;
  font-family: 'Roboto', sans-serif;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 1px #2196F3;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SuccessMessage = styled(motion.div)`
  background-color: rgba(0, 200, 83, 0.1);
  border: 1px solid #00C853;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
  text-align: center;
  
  p {
    color: #00C853;
    font-weight: 500;
    margin: 0;
  }
`;

const ErrorMessage = styled.p`
  color: #FF5252;
  font-size: 14px;
  margin-top: 4px;
`;

const AboutSection = styled.div`
  background-color: #1E1E1E;
  border-radius: 12px;
  padding: 32px;
  margin-top: 40px;
  border: 1px solid #333;
  text-align: center;
`;

const AboutText = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 24px;
  line-height: 1.6;
`;

const Contact = () => {
  useEffect(() => {
    trackPageView('Contact', window.location.pathname);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear errors when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate form submission (replace with actual form handling)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ ...errors, submit: 'There was an error. Please try again.' });
    }
    
    setIsSubmitting(false);
  };

  return (
    <>
      <SEO 
        title="Contact CarConsul - Get in Touch"
        description="Contact CarConsul for support, questions, or feedback about our AI-powered car buying advisor"
        pagePath="contact"
      />
      <PageContainer>
        <Content
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Title>Contact Us</Title>
          <Subtitle>
            Get in touch with our team. We're here to help with your car buying journey.
          </Subtitle>

          <ContactGrid>
            <ContactInfo>
              <SectionTitle>Get in Touch</SectionTitle>
              
              <ContactItem>
                <ContactLabel>Email</ContactLabel>
                <ContactValue>
                  <a href="mailto:nick@carconsul.com">nick@carconsul.com</a>
                </ContactValue>
              </ContactItem>
              
              <ContactItem>
                <ContactLabel>Response Time</ContactLabel>
                <ContactValue>
                  We typically respond within 24 hours during business days
                </ContactValue>
              </ContactItem>
              
              <ContactItem>
                <ContactLabel>Business Hours</ContactLabel>
                <ContactValue>
                  Monday - Friday: 9:00 AM - 6:00 PM EST<br />
                  Saturday - Sunday: Limited support
                </ContactValue>
              </ContactItem>
              
              <ContactItem>
                <ContactLabel>Support Types</ContactLabel>
                <ContactValue>
                  • Technical support<br />
                  • Account questions<br />
                  • Feature requests<br />
                  • General inquiries<br />
                  • Partnership opportunities
                </ContactValue>
              </ContactItem>
            </ContactInfo>

            <ContactForm>
              <SectionTitle>Send us a Message</SectionTitle>
              
              {!submitted ? (
                <Form onSubmit={handleSubmit}>
                  <InputGroup>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                  </InputGroup>
                  
                  <InputGroup>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                  </InputGroup>
                  
                  <InputGroup>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder="What is this regarding?"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                    {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
                  </InputGroup>
                  
                  <InputGroup>
                    <Label htmlFor="message">Message *</Label>
                    <TextArea
                      id="message"
                      name="message"
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={handleChange}
                    />
                    {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
                  </InputGroup>
                  
                  {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                    disabled={isSubmitting}
                    style={{ marginTop: '8px' }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Form>
              ) : (
                <SuccessMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <p>Thank you for your message! We'll get back to you within 24 hours.</p>
                </SuccessMessage>
              )}
            </ContactForm>
          </ContactGrid>

          <AboutSection>
            <SectionTitle>About CarConsul</SectionTitle>
            <AboutText>
              CarConsul is an AI-powered car buying advisor that helps you make smarter automotive decisions. 
              We provide independent, unbiased advice to help you find the perfect vehicle at the best price.
            </AboutText>
            <AboutText>
              Founded by automotive experts and AI specialists, we're committed to revolutionizing the car 
              buying experience by putting the power of information and negotiation back in your hands.
            </AboutText>
            <AboutText>
              Have questions about our service? Want to suggest a feature? Or looking to partner with us? 
              We'd love to hear from you!
            </AboutText>
          </AboutSection>
        </Content>
      </PageContainer>
    </>
  );
};

export default Contact;